<template>
  <div v-if="isLoadingRef" class="flex min-h-dvh items-center justify-center">
    <LoadingContent :text="t('admin.setup.loading')" />
  </div>

  <div v-else-if="isErrorRef" class="flex min-h-dvh items-center justify-center p-6">
    <p role="alert" class="max-w-xl text-sm" :class="isForbiddenRef ? '' : 'text-destructive'">
      {{ isForbiddenRef ? t('admin.platform.forbidden') : t('admin.setup.errors.loading') }}
    </p>
  </div>

  <div
    v-else
    class="flex h-dvh flex-col overflow-y-auto bg-background text-foreground md:flex-row md:items-start"
    data-test-id="platform-setup"
  >
    <SetupStepRail
      :current="screenRef"
      :completed-steps="completedStepsRef"
      :visited-steps="visitedStepsRef"
      @select="goTo"
      @later="later"
    />

    <main
      id="main-content"
      tabindex="-1"
      class="flex min-w-0 flex-1 flex-col gap-8 px-4 pt-8 pb-12 outline-none md:px-14 md:pt-12 md:pb-16"
    >
      <!-- Largeur bornée : sur un grand écran, les champs et les boutons restent groupés. -->
      <div class="mx-auto flex w-full max-w-4xl flex-col gap-8">
        <div
          v-if="screenRef === 'identity' && isPendingStatusRef"
          role="status"
          class="flex max-w-3xl items-start gap-3 rounded-lg bg-[#e7f4ee] px-4 py-3.5 text-[#0f5e3e]"
          data-test-id="platform-setup-created"
        >
          <CircleCheckIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <span class="flex flex-col gap-0.5 text-sm leading-normal">
            <strong>{{
              t('admin.setup.created.title', { organization: form.COMPANY_NAME || savedRef.COMPANY_NAME || '' })
            }}</strong>
            <span>{{ t('admin.setup.created.description') }}</span>
          </span>
        </div>

        <header class="flex max-w-3xl flex-col gap-2">
          <span class="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
            {{ t('admin.setup.stepCounter', { current: stepIndexRef + 1, total: SETUP_STEPS.length }) }}
          </span>
          <h1
            ref="headingRef"
            tabindex="-1"
            class="font-display text-2xl leading-tight font-semibold tracking-tight outline-none md:text-3xl"
            data-test-id="platform-setup-title"
          >
            {{ titleRef }}
          </h1>
          <p v-if="introRef" class="text-base leading-relaxed text-muted-foreground">{{ introRef }}</p>
        </header>

        <StepIdentity v-if="screenRef === 'identity'" />
        <StepBranding v-else-if="screenRef === 'branding'" />
        <StepLogin v-else-if="screenRef === 'login'" />
        <StepContact v-else-if="screenRef === 'contact'" />
        <StepTeam v-else-if="screenRef === 'team'" />
        <StepRecap v-else-if="screenRef === 'recap'" @edit="goTo" />

        <p v-if="errorRef" role="alert" class="max-w-3xl text-sm text-destructive" data-test-id="platform-setup-error">
          {{ errorRef }}
        </p>

        <footer class="flex flex-wrap items-center gap-3 border-t pt-6">
          <Button v-if="stepIndexRef > 0" variant="outline" :disabled="isSavingRef" @click="back">
            {{ t('admin.setup.actions.back') }}
          </Button>
          <span class="flex-auto" />
          <template v-if="screenRef === 'recap'">
            <Button variant="ghost" :disabled="isSavingRef" data-test-id="platform-setup-finish" @click="finish(false)">
              {{ t('admin.setup.recap.finish') }}
            </Button>
            <Button :disabled="isSavingRef" data-test-id="platform-setup-finish-create" @click="finish(true)">
              <Spinner v-if="isSavingRef" aria-hidden="true" />
              {{ t('admin.setup.recap.finishAndCreate') }}
            </Button>
          </template>
          <template v-else>
            <Button variant="ghost" :disabled="isSavingRef" data-test-id="platform-setup-skip" @click="next">
              {{ t('admin.setup.actions.skip') }}
            </Button>
            <Button :disabled="isSavingRef" data-test-id="platform-setup-continue" @click="saveAndContinue">
              <Spinner v-if="isSavingRef" aria-hidden="true" />
              {{ t('admin.setup.actions.continue') }}
            </Button>
          </template>
        </footer>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { CircleCheckIcon } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import SetupStepRail from '@/features/admin/platform-setup/components/setup-step-rail.vue'
import StepBranding from '@/features/admin/platform-setup/components/steps/step-branding.vue'
import StepContact from '@/features/admin/platform-setup/components/steps/step-contact.vue'
import StepIdentity from '@/features/admin/platform-setup/components/steps/step-identity.vue'
import StepLogin from '@/features/admin/platform-setup/components/steps/step-login.vue'
import StepRecap from '@/features/admin/platform-setup/components/steps/step-recap.vue'
import StepTeam from '@/features/admin/platform-setup/components/steps/step-team.vue'
import type { SetupStepType } from '@/features/admin/platform-setup/constants'
import { PLATFORM_SETUP_STATUS, SETUP_STEPS } from '@/features/admin/platform-setup/constants'
import { LoginStepError, providePlatformSetup } from '@/features/admin/platform-setup/hooks/use-platform-setup.hook'
import useSuperAdmins from '@/features/admin/platform-setup/hooks/use-super-admins.hook'
import { isStepComplete } from '@/features/admin/platform-setup/utils/checklist'
import { markSetupDismissed } from '@/features/admin/platform-setup/utils/dismissal'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import useRouter from '@/hooks/use-router.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { ROUTES } from '@/utils/config/constants'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const { form, savedRef, checklistRef, isLoadingRef, isErrorRef, isForbiddenRef, isSavingRef, saveStep, complete } =
  providePlatformSetup()
const { whiteLabelRef } = useWhiteLabel()
// Juste après la création de la plateforme, l'étape 1 confirme qu'elle existe.
const isPendingStatusRef = computed(() => whiteLabelRef.value.platformSetupStatus === PLATFORM_SETUP_STATUS.PENDING)

const isSetupStep = (value: unknown): value is SetupStepType => SETUP_STEPS.includes(value as SetupStepType)

// L'étape vit dans l'URL : la checklist du tableau de bord y renvoie directement.
const screenRef = computed<SetupStepType>(() => {
  const step = route.query.step
  return isSetupStep(step) ? step : 'identity'
})
const stepIndexRef = computed(() => SETUP_STEPS.indexOf(screenRef.value))

const { hasTeamRef } = useSuperAdmins()

const visitedStepsRef = ref<SetupStepType[]>([])
// Les étapes réglées par la checklist ; l'équipe est facultative et n'en fait pas partie.
const CHECKLIST_STEPS = SETUP_STEPS.filter((step) => step !== 'recap' && step !== 'team')
const completedStepsRef = computed(() => [
  ...CHECKLIST_STEPS.filter((step) => isStepComplete(checklistRef.value, step)),
  ...(hasTeamRef.value ? (['team'] as const) : []),
])

const errorRef = ref('')
const headingRef = ref<HTMLElement | null>(null)

// Tous les points de la checklist sont réglés.
const isAllCompleteRef = computed(() => CHECKLIST_STEPS.every((step) => completedStepsRef.value.includes(step)))

const titleRef = computed(() => {
  if (screenRef.value === 'recap') {
    return isAllCompleteRef.value ? t('admin.setup.recap.titleDone') : t('admin.setup.recap.title')
  }
  return t(`admin.setup.${screenRef.value}.title`)
})

const STEPS_WITH_INTRO: SetupStepType[] = ['identity', 'branding', 'login', 'team']
const introRef = computed(() => {
  if (screenRef.value === 'recap') return isAllCompleteRef.value ? '' : t('admin.setup.recap.intro')
  return STEPS_WITH_INTRO.includes(screenRef.value) ? t(`admin.setup.${screenRef.value}.intro`) : ''
})

watch(
  screenRef,
  async (screen, previous) => {
    errorRef.value = ''
    if (previous && !visitedStepsRef.value.includes(previous)) {
      visitedStepsRef.value.push(previous)
    }
    // Au changement d'étape, le focus va au titre pour que le lecteur d'écran l'annonce.
    if (previous !== undefined) {
      await nextTick()
      headingRef.value?.focus()
    }
  },
  { immediate: true },
)

function goTo(step: SetupStepType) {
  router?.push({ query: { step } }).catch(() => {})
}

function next() {
  const nextStep = SETUP_STEPS[stepIndexRef.value + 1]
  if (nextStep) goTo(nextStep)
}

function back() {
  if (stepIndexRef.value > 0) goTo(SETUP_STEPS[stepIndexRef.value - 1])
}

async function saveAndContinue() {
  errorRef.value = ''
  try {
    await saveStep(screenRef.value)
    next()
  } catch (error) {
    errorRef.value = error instanceof LoginStepError ? t(error.message) : t('admin.setup.errors.save')
  }
}

async function finish(createProgram: boolean) {
  errorRef.value = ''
  try {
    await complete()
    router?.push(createProgram ? ROUTES.ADMIN.NEW_PROGRAM_SETTINGS : ROUTES.ADMIN.DASHBOARD).catch(() => {})
  } catch {
    errorRef.value = t('admin.setup.errors.save')
  }
}

function later() {
  markSetupDismissed()
  router?.push(ROUTES.ADMIN.DASHBOARD).catch(() => {})
}
</script>
